import type { TravelModule, TradingModule } from '@polatkan/api/permissions';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { LoginPage } from '@/pages/auth/login';
import {
  PermissionGuard,
  AppGuard,
  AdminGuard,
  NoAccessFallback,
} from '@/components/guards/permission-guard';

// Travel Pages
import { DashboardPage } from '@/pages/travel/dashboard';
import { TravelCariAccountPage } from '@/pages/travel/cari/detail';
import { TravelCariAccountGuard } from '@/components/travel/cari/account-guard';
import { LegacyTravelCariRedirect } from '@/components/travel/cari/legacy-redirect';
import { PackagesPage } from '@/pages/travel/packages';
import { PackageDetailPage } from '@/pages/travel/packages/detail';
import { QuotesPage } from '@/pages/travel/quotes';
import { QuoteDetailPage } from '@/pages/travel/quotes/detail';
import { BookingsPage } from '@/pages/travel/bookings';
import { NewBookingPage } from '@/pages/travel/bookings/new';
import { BookingDetailPage } from '@/pages/travel/bookings/detail';
import { InvoicesPage } from '@/pages/travel/invoices';
import { InvoiceDetailPage } from '@/pages/travel/invoices/detail';
import { AccountingPage } from '@/pages/travel/accounting';
import { AccountDetailPage } from '@/pages/travel/accounting/accounts/detail';
import { JournalEntryFormPage } from '@/pages/travel/accounting/journal-entries/form';
import { JournalEntryDetailPage } from '@/pages/travel/accounting/journal-entries/detail';
import { ReportsPage } from '@/pages/travel/accounting/reports';
import { ReconciliationPage } from '@/pages/travel/accounting/reconciliation';
import { BankImportPage } from '@/pages/travel/accounting/reconciliation/import';
import { FiscalPeriodsPage } from '@/pages/travel/accounting/periods';
import { TravelPostingRulesPage } from '@/pages/travel/accounting/posting-rules';
import { TravelTieOutPage } from '@/pages/travel/accounting/tie-out';
import { TravelPeriodEndPage } from '@/pages/travel/accounting/period-end';
import { TravelCariKartlarPage } from '@/pages/travel/cari';
import { TravelCariTypesPage } from '@/pages/travel/cari/types';
import { TravelCariBalancesPage } from '@/pages/travel/cari/balances';
import { TravelCariTransactionsPage } from '@/pages/travel/cari/transactions';
import { TravelCariLedgerPage } from '@/pages/travel/cari/movements';
import { TravelCariMizanPage } from '@/pages/travel/cari/mizan';
import { ExpensesPage } from '@/pages/travel/expenses';
import { ExpenseDetailPage } from '@/pages/travel/expenses/detail';
import { HotelBookingsPage } from '@/pages/travel/hotels';
import { HotelBookingDetailPage } from '@/pages/travel/hotels/detail';
import { AllocationsPage } from '@/pages/travel/allocations';
import { AllocationDetailPage } from '@/pages/travel/allocations/detail';
import { AllocationsCalendarPage } from '@/pages/travel/allocations/calendar';
import PromoCodesPage from '@/pages/travel/marketing/promo-codes';
import ReviewsPage from '@/pages/travel/reviews';
import { NotificationsPage } from '@/pages/travel/notifications';
import { OperationsPage } from '@/pages/travel/operations';
import { OperationsCalendarPage } from '@/pages/travel/operations/calendar';
import { ManifestPage } from '@/pages/travel/operations/manifest';
import { ActivityFeedPage } from '@/pages/travel/activity-feed';

// Trading Pages
import { TradingDashboardPage } from '@/pages/trading/dashboard';
import { TradingBrandsPage } from '@/pages/trading/brands';
import { TradingModelsPage } from '@/pages/trading/models';
import { ModelDetailPage } from '@/pages/trading/models/[id]';
import { TradingProductsPage } from '@/pages/trading/products';
import { TradingProductDetailPage } from '@/pages/trading/products/[id]';
import { NewTradingProductPage } from '@/pages/trading/products/new';
import { TradingInquiriesPage } from '@/pages/trading/inquiries';
import { WarehousesPage } from '@/pages/trading/warehouses';
import { StockLevelsPage } from '@/pages/trading/inventory';
import { StockMovementsPage } from '@/pages/trading/inventory/movements';
// SuppliersPage redirected to /trading/cari?type=T
import { PurchaseOrdersPage } from '@/pages/trading/purchasing';
import { NewPurchaseOrderPage } from '@/pages/trading/purchasing/new';
import { PurchaseOrderDetailPage } from '@/pages/trading/purchasing/[id]';
import { PurchaseOrderEditPage } from '@/pages/trading/purchasing/[id]/edit';
import { GoodsReceiptsPage } from '@/pages/trading/purchasing/receipts';
import { NewGoodsReceiptPage } from '@/pages/trading/purchasing/receipts/new';
import { GoodsReceiptDetailPage } from '@/pages/trading/purchasing/receipts/[id]';
import { EditLandedCostsPage } from '@/pages/trading/purchasing/receipts/edit-landed-costs';
import { SalesQuotesPage } from '@/pages/trading/sales/quotes';
import { NewSalesQuotePage } from '@/pages/trading/sales/quotes/new';
import { QuoteDetailPage as SalesQuoteDetailPage } from '@/pages/trading/sales/quotes/[id]';
import { QuoteEditPage } from '@/pages/trading/sales/quotes/[id]/edit';
import { SalesOrdersPage } from '@/pages/trading/sales/orders';
import { NewSalesOrderPage } from '@/pages/trading/sales/orders/new';
import { OrderDetailPage } from '@/pages/trading/sales/orders/[id]';
import { OrderEditPage } from '@/pages/trading/sales/orders/[id]/edit';
import { DeliveriesPage } from '@/pages/trading/sales/deliveries';
import { NewDeliveryPage } from '@/pages/trading/sales/deliveries/new';
import { DeliveryDetailPage } from '@/pages/trading/sales/deliveries/[id]';
import { TradingInvoicesPage } from '@/pages/trading/finance/invoices';
import { TradingInvoiceDetailPage } from '@/pages/trading/finance/invoices/[id]';
import { TradingInvoiceEditPage } from '@/pages/trading/finance/invoices/[id]/edit';
import { TradingPaymentsPage } from '@/pages/trading/finance/payments';
import { NewPaymentPage } from '@/pages/trading/finance/payments/new';
import { PaymentDetailPage } from '@/pages/trading/finance/payments/[id]';
import { SupplierInvoicesPage } from '@/pages/trading/finance/supplier-invoices';
import { SupplierInvoiceDetailPage } from '@/pages/trading/finance/supplier-invoices/[id]';
import { CreditNotesPage } from '@/pages/trading/finance/credit-notes';
import { CreditNoteDetailPage } from '@/pages/trading/finance/credit-notes/[id]';
import { ConsignmentListPage } from '@/pages/trading/consignment';
import { ConsignmentDetailPage } from '@/pages/trading/consignment/detail';
import { NewConsignmentPage } from '@/pages/trading/consignment/new';
import { ConsignmentVendorsPage } from '@/pages/trading/consignment/vendors';
import { TradingCustomerDetailPage } from '@/pages/trading/customers/detail';
import { CariKartlarPage } from '@/pages/trading/cari';
import { CariTypesPage } from '@/pages/trading/cari/types';
import { SellerDetailPage } from '@/pages/trading/cari/sellers/detail';
import { SupplierDetailPage } from '@/pages/trading/cari/suppliers/detail';
import { DealerDetailPage } from '@/pages/trading/cari/dealers/detail';
import { GenericCariDetailPage } from '@/pages/trading/cari/generic/detail';
import { CariMovementsPage } from '@/pages/trading/cari/movements';
import { CariBalancesPage } from '@/pages/trading/cari/balances';
import { CariTransactionsPage } from '@/pages/trading/cari/transactions';
import { BankMatchingPage } from '@/pages/trading/cari/bank-matching';
import { CariMizanPage } from '@/pages/trading/cari/mizan';
import { TradingReportsPage as TradingStockReportsPage } from '@/pages/trading/reports';
import { TradingAgingReportPage } from '@/pages/trading/reports/aging';

// Trading Accounting Pages
import { TradingAccountingPage } from '@/pages/trading/accounting';
import { TradingAccountDetailPage } from '@/pages/trading/accounting/accounts/detail';
import { TradingJournalEntryFormPage } from '@/pages/trading/accounting/journal-entries/form';
import { TradingJournalEntryDetailPage } from '@/pages/trading/accounting/journal-entries/detail';
import { TradingReportsPage } from '@/pages/trading/accounting/reports';
import { TradingFiscalPeriodsPage } from '@/pages/trading/accounting/periods';
import { TradingPostingRulesPage } from '@/pages/trading/accounting/posting-rules';
import { TradingTieOutPage } from '@/pages/trading/accounting/tie-out';
import { TradingPeriodEndPage } from '@/pages/trading/accounting/period-end';
import { TradingActivityFeedPage } from '@/pages/trading/activity-feed';

// Settings Pages (Shared)
import { SettingsPage } from '@/pages/settings';
import { TeamSettingsPage } from '@/pages/settings/team';
import { EmailTemplatesPage } from '@/pages/settings/email-templates';
import { PaymentPlanTemplatesPage } from '@/pages/settings/payment-plan-templates';
import { UserPermissionsPage } from '@/pages/settings/user-permissions';

import { useAuthStore } from '@/stores/auth-store';
import { DevelopmentPreviewNotice } from '@/components/travel/cari/development-preview-notice';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground" role="status">Loading session...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <><DevelopmentPreviewNotice />{children}</>;
}

// Wrapper for Travel routes with app access check
function TravelRoute({ children, module, action = 'VIEW' }: {
  children: React.ReactNode;
  module: TravelModule;
  action?: 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT' | 'APPROVE';
}) {
  return (
    <AppGuard app="TRAVEL" fallback={<NoAccessFallback message="You don't have access to the Travel application." />}>
      <PermissionGuard
        app="TRAVEL"
        module={module}
        action={action}
        fallback={<NoAccessFallback />}
      >
        {children}
      </PermissionGuard>
    </AppGuard>
  );
}

// Wrapper for Trading routes with app access check
function TradingRoute({ children, module, action = 'VIEW' }: {
  children: React.ReactNode;
  module: TradingModule;
  action?: 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT' | 'APPROVE';
}) {
  return (
    <AppGuard app="TRADING" fallback={<NoAccessFallback message="You don't have access to the Trading application." />}>
      <PermissionGuard
        app="TRADING"
        module={module}
        action={action}
        fallback={<NoAccessFallback />}
      >
        {children}
      </PermissionGuard>
    </AppGuard>
  );
}

function App() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect to travel dashboard */}
        <Route index element={<Navigate to="/travel" replace />} />

        {/* Travel Routes */}
        <Route path="travel" element={
          <TravelRoute module="DASHBOARD"><DashboardPage /></TravelRoute>
        } />
        <Route path="travel/customers" element={
          <LegacyTravelCariRedirect source="customer" />
        } />
        <Route path="travel/customers/:id" element={
          <LegacyTravelCariRedirect source="customer" />
        } />
        <Route path="travel/partners" element={
          <LegacyTravelCariRedirect source="partner" />
        } />
        <Route path="travel/partners/:id" element={
          <LegacyTravelCariRedirect source="partner" />
        } />
        <Route path="travel/packages" element={
          <TravelRoute module="PACKAGES"><PackagesPage /></TravelRoute>
        } />
        <Route path="travel/packages/:id" element={
          <TravelRoute module="PACKAGES"><PackageDetailPage /></TravelRoute>
        } />
        <Route path="travel/quotes" element={
          <TravelRoute module="QUOTES"><QuotesPage /></TravelRoute>
        } />
        <Route path="travel/quotes/:id" element={
          <TravelRoute module="QUOTES"><QuoteDetailPage /></TravelRoute>
        } />
        <Route path="travel/bookings" element={
          <TravelRoute module="BOOKINGS"><BookingsPage /></TravelRoute>
        } />
        <Route path="travel/bookings/new" element={
          <TravelRoute module="BOOKINGS" action="CREATE"><NewBookingPage /></TravelRoute>
        } />
        <Route path="travel/bookings/:id" element={
          <TravelRoute module="BOOKINGS"><BookingDetailPage /></TravelRoute>
        } />
        <Route path="travel/hotels" element={
          <TravelRoute module="BOOKINGS"><HotelBookingsPage /></TravelRoute>
        } />
        <Route path="travel/hotels/:id" element={
          <TravelRoute module="BOOKINGS"><HotelBookingDetailPage /></TravelRoute>
        } />
        <Route path="travel/invoices" element={
          <TravelRoute module="INVOICES"><InvoicesPage /></TravelRoute>
        } />
        <Route path="travel/invoices/:id" element={
          <TravelRoute module="INVOICES"><InvoiceDetailPage /></TravelRoute>
        } />
        <Route path="travel/expenses" element={
          <TravelRoute module="EXPENSES"><ExpensesPage /></TravelRoute>
        } />
        <Route path="travel/expenses/:id" element={
          <TravelRoute module="EXPENSES"><ExpenseDetailPage /></TravelRoute>
        } />
        <Route path="travel/accounting" element={
          <TravelRoute module="ACCOUNTING"><AccountingPage /></TravelRoute>
        } />
        <Route path="travel/accounting/accounts/:id" element={
          <TravelRoute module="ACCOUNTING"><AccountDetailPage /></TravelRoute>
        } />
        <Route path="travel/accounting/journal-entries/new" element={
          <TravelRoute module="ACCOUNTING" action="CREATE"><JournalEntryFormPage /></TravelRoute>
        } />
        <Route path="travel/accounting/journal-entries/:id" element={
          <TravelRoute module="ACCOUNTING"><JournalEntryDetailPage /></TravelRoute>
        } />
        <Route path="travel/accounting/journal-entries/:id/edit" element={
          <TravelRoute module="ACCOUNTING" action="EDIT"><JournalEntryFormPage /></TravelRoute>
        } />
        <Route path="travel/accounting/reports" element={
          <TravelRoute module="ACCOUNTING"><ReportsPage /></TravelRoute>
        } />
        <Route path="travel/accounting/reconciliation" element={
          <TravelRoute module="ACCOUNTING"><ReconciliationPage /></TravelRoute>
        } />
        <Route path="travel/accounting/reconciliation/import" element={
          <TravelRoute module="ACCOUNTING" action="CREATE"><BankImportPage /></TravelRoute>
        } />
        <Route path="travel/accounting/periods" element={
          <TravelRoute module="ACCOUNTING"><FiscalPeriodsPage /></TravelRoute>
        } />
        <Route path="travel/accounting/posting-rules" element={
          <TravelRoute module="ACCOUNTING"><TravelPostingRulesPage /></TravelRoute>
        } />
        <Route path="travel/accounting/tie-out" element={
          <TravelRoute module="ACCOUNTING"><TravelTieOutPage /></TravelRoute>
        } />
        <Route path="travel/accounting/period-end" element={
          <TravelRoute module="ACCOUNTING"><TravelPeriodEndPage /></TravelRoute>
        } />
        <Route path="travel/cari" element={
          <TravelCariAccountGuard><TravelCariKartlarPage /></TravelCariAccountGuard>
        } />
        <Route path="travel/cari/accounts/:source/:id" element={<TravelCariAccountGuard><TravelCariAccountPage /></TravelCariAccountGuard>} />
        <Route path="travel/cari/types" element={<TravelRoute module="ACCOUNTING"><TravelCariTypesPage /></TravelRoute>} />
        <Route path="travel/cari/balances" element={
          <TravelRoute module="ACCOUNTING"><TravelCariBalancesPage /></TravelRoute>
        } />
        <Route path="travel/cari/transactions" element={
          <TravelRoute module="ACCOUNTING"><TravelCariTransactionsPage /></TravelRoute>
        } />
        <Route path="travel/cari/mizan" element={
          <TravelRoute module="ACCOUNTING"><TravelCariMizanPage /></TravelRoute>
        } />
        <Route path="travel/cari/:cariCode/movements" element={
          <TravelRoute module="ACCOUNTING"><TravelCariLedgerPage /></TravelRoute>
        } />
        <Route path="travel/allocations" element={
          <TravelRoute module="ALLOCATIONS"><AllocationsPage /></TravelRoute>
        } />
        <Route path="travel/allocations/calendar" element={
          <TravelRoute module="ALLOCATIONS"><AllocationsCalendarPage /></TravelRoute>
        } />
        <Route path="travel/allocations/:id" element={
          <TravelRoute module="ALLOCATIONS"><AllocationDetailPage /></TravelRoute>
        } />
        <Route path="travel/marketing/promo-codes" element={
          <TravelRoute module="PROMO_CODES"><PromoCodesPage /></TravelRoute>
        } />
        <Route path="travel/reviews" element={
          <TravelRoute module="REVIEWS"><ReviewsPage /></TravelRoute>
        } />
        <Route path="travel/notifications" element={
          <TravelRoute module="NOTIFICATIONS"><NotificationsPage /></TravelRoute>
        } />
        <Route path="travel/operations" element={
          <TravelRoute module="OPERATIONS"><OperationsPage /></TravelRoute>
        } />
        <Route path="travel/operations/calendar" element={
          <TravelRoute module="OPERATIONS"><OperationsCalendarPage /></TravelRoute>
        } />
        <Route path="travel/operations/manifest/:id" element={
          <TravelRoute module="OPERATIONS"><ManifestPage /></TravelRoute>
        } />
        <Route path="travel/activity-feed" element={
          <TravelRoute module="OPERATIONS"><ActivityFeedPage /></TravelRoute>
        } />

        {/* Trading Routes */}
        <Route path="trading" element={
          <TradingRoute module="DASHBOARD"><TradingDashboardPage /></TradingRoute>
        } />
        <Route path="trading/cari" element={
          <TradingRoute module="CUSTOMERS"><CariKartlarPage /></TradingRoute>
        } />
        <Route path="trading/cari/balances" element={
          <TradingRoute module="CUSTOMERS"><CariBalancesPage /></TradingRoute>
        } />
        <Route path="trading/cari/transactions" element={
          <TradingRoute module="CUSTOMERS"><CariTransactionsPage /></TradingRoute>
        } />
        <Route path="trading/cari/bank-matching" element={
          <TradingRoute module="CUSTOMERS"><BankMatchingPage /></TradingRoute>
        } />
        <Route path="trading/cari/mizan" element={
          <TradingRoute module="CUSTOMERS"><CariMizanPage /></TradingRoute>
        } />
        <Route path="trading/cari/types" element={
          <TradingRoute module="ACCOUNTING"><CariTypesPage /></TradingRoute>
        } />
        <Route path="trading/cari/sellers/:id" element={
          <TradingRoute module="CUSTOMERS"><SellerDetailPage /></TradingRoute>
        } />
        <Route path="trading/cari/suppliers/:id" element={
          <TradingRoute module="CUSTOMERS"><SupplierDetailPage /></TradingRoute>
        } />
        <Route path="trading/cari/dealers/:id" element={
          <TradingRoute module="CUSTOMERS"><DealerDetailPage /></TradingRoute>
        } />
        <Route path="trading/cari/generic/:type/:id" element={
          <TradingRoute module="CUSTOMERS"><GenericCariDetailPage /></TradingRoute>
        } />
        <Route path="trading/cari/:source/:id/movements" element={
          <TradingRoute module="CUSTOMERS"><CariMovementsPage /></TradingRoute>
        } />
        <Route path="trading/reports" element={
          <Navigate to="/trading/reports/stock-ledger" replace />
        } />
        <Route path="trading/reports/aging" element={
          <TradingRoute module="INVOICES"><TradingAgingReportPage /></TradingRoute>
        } />
        <Route path="trading/reports/:report" element={
          <TradingRoute module="STOCK_MOVEMENTS"><TradingStockReportsPage /></TradingRoute>
        } />
        <Route path="trading/customers" element={
          <Navigate to="/trading/cari?type=M" replace />
        } />
        <Route path="trading/customers/:id" element={
          <TradingRoute module="CUSTOMERS"><TradingCustomerDetailPage /></TradingRoute>
        } />
        <Route path="trading/brands" element={
          <TradingRoute module="BRANDS"><TradingBrandsPage /></TradingRoute>
        } />
        <Route path="trading/models" element={
          <TradingRoute module="MODELS"><TradingModelsPage /></TradingRoute>
        } />
        <Route path="trading/models/:id" element={
          <TradingRoute module="MODELS"><ModelDetailPage /></TradingRoute>
        } />
        <Route path="trading/products" element={
          <TradingRoute module="PRODUCTS"><TradingProductsPage /></TradingRoute>
        } />
        <Route path="trading/products/new" element={
          <TradingRoute module="PRODUCTS"><NewTradingProductPage /></TradingRoute>
        } />
        <Route path="trading/products/:id" element={
          <TradingRoute module="PRODUCTS"><TradingProductDetailPage /></TradingRoute>
        } />
        <Route path="trading/inquiries" element={
          <TradingRoute module="INQUIRIES"><TradingInquiriesPage /></TradingRoute>
        } />
        <Route path="trading/warehouses" element={
          <TradingRoute module="WAREHOUSES"><WarehousesPage /></TradingRoute>
        } />
        <Route path="trading/inventory" element={
          <TradingRoute module="STOCK_LEVELS"><StockLevelsPage /></TradingRoute>
        } />
        <Route path="trading/inventory/movements" element={
          <TradingRoute module="STOCK_MOVEMENTS"><StockMovementsPage /></TradingRoute>
        } />
        <Route path="trading/suppliers" element={
          <Navigate to="/trading/cari?type=T" replace />
        } />
        <Route path="trading/purchasing" element={
          <TradingRoute module="PURCHASE_ORDERS"><PurchaseOrdersPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/new" element={
          <TradingRoute module="PURCHASE_ORDERS" action="CREATE"><NewPurchaseOrderPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/:id" element={
          <TradingRoute module="PURCHASE_ORDERS"><PurchaseOrderDetailPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/:id/edit" element={
          <TradingRoute module="PURCHASE_ORDERS" action="EDIT"><PurchaseOrderEditPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/receipts" element={
          <TradingRoute module="GOODS_RECEIPTS"><GoodsReceiptsPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/receipts/new" element={
          <TradingRoute module="GOODS_RECEIPTS" action="CREATE"><NewGoodsReceiptPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/receipts/:id" element={
          <TradingRoute module="GOODS_RECEIPTS"><GoodsReceiptDetailPage /></TradingRoute>
        } />
        <Route path="trading/purchasing/receipts/:id/edit-landed-costs" element={
          <TradingRoute module="GOODS_RECEIPTS" action="EDIT"><EditLandedCostsPage /></TradingRoute>
        } />
        <Route path="trading/sales/quotes" element={
          <TradingRoute module="SALES_QUOTES"><SalesQuotesPage /></TradingRoute>
        } />
        <Route path="trading/sales/quotes/new" element={
          <TradingRoute module="SALES_QUOTES" action="CREATE"><NewSalesQuotePage /></TradingRoute>
        } />
        <Route path="trading/sales/quotes/:id" element={
          <TradingRoute module="SALES_QUOTES"><SalesQuoteDetailPage /></TradingRoute>
        } />
        <Route path="trading/sales/quotes/:id/edit" element={
          <TradingRoute module="SALES_QUOTES" action="EDIT"><QuoteEditPage /></TradingRoute>
        } />
        <Route path="trading/sales/orders" element={
          <TradingRoute module="SALES_ORDERS"><SalesOrdersPage /></TradingRoute>
        } />
        <Route path="trading/sales/orders/new" element={
          <TradingRoute module="SALES_ORDERS" action="CREATE"><NewSalesOrderPage /></TradingRoute>
        } />
        <Route path="trading/sales/orders/:id" element={
          <TradingRoute module="SALES_ORDERS"><OrderDetailPage /></TradingRoute>
        } />
        <Route path="trading/sales/orders/:id/edit" element={
          <TradingRoute module="SALES_ORDERS" action="EDIT"><OrderEditPage /></TradingRoute>
        } />
        <Route path="trading/sales/deliveries" element={
          <TradingRoute module="DELIVERIES"><DeliveriesPage /></TradingRoute>
        } />
        <Route path="trading/sales/deliveries/new" element={
          <TradingRoute module="DELIVERIES" action="CREATE"><NewDeliveryPage /></TradingRoute>
        } />
        <Route path="trading/sales/deliveries/:id" element={
          <TradingRoute module="DELIVERIES"><DeliveryDetailPage /></TradingRoute>
        } />
        <Route path="trading/finance/invoices" element={
          <TradingRoute module="INVOICES"><TradingInvoicesPage /></TradingRoute>
        } />
        <Route path="trading/finance/invoices/:id" element={
          <TradingRoute module="INVOICES"><TradingInvoiceDetailPage /></TradingRoute>
        } />
        <Route path="trading/finance/invoices/:id/edit" element={
          <TradingRoute module="INVOICES"><TradingInvoiceEditPage /></TradingRoute>
        } />
        <Route path="trading/finance/payments" element={
          <TradingRoute module="PAYMENTS"><TradingPaymentsPage /></TradingRoute>
        } />
        <Route path="trading/finance/payments/new" element={
          <TradingRoute module="PAYMENTS"><NewPaymentPage /></TradingRoute>
        } />
        <Route path="trading/finance/payments/:id" element={
          <TradingRoute module="PAYMENTS"><PaymentDetailPage /></TradingRoute>
        } />
        <Route path="trading/finance/supplier-invoices" element={
          <TradingRoute module="SUPPLIER_INVOICES"><SupplierInvoicesPage /></TradingRoute>
        } />
        <Route path="trading/finance/supplier-invoices/:id" element={
          <TradingRoute module="SUPPLIER_INVOICES"><SupplierInvoiceDetailPage /></TradingRoute>
        } />
        <Route path="trading/finance/credit-notes" element={
          <TradingRoute module="CREDIT_NOTES"><CreditNotesPage /></TradingRoute>
        } />
        <Route path="trading/finance/credit-notes/:id" element={
          <TradingRoute module="CREDIT_NOTES"><CreditNoteDetailPage /></TradingRoute>
        } />
        <Route path="trading/consignment" element={
          <TradingRoute module="CONSIGNMENT"><ConsignmentListPage /></TradingRoute>
        } />
        <Route path="trading/consignment/new" element={
          <TradingRoute module="CONSIGNMENT" action="CREATE"><NewConsignmentPage /></TradingRoute>
        } />
        <Route path="trading/consignment/vendors" element={
          <TradingRoute module="CONSIGNMENT"><ConsignmentVendorsPage /></TradingRoute>
        } />
        <Route path="trading/consignment/:id" element={
          <TradingRoute module="CONSIGNMENT"><ConsignmentDetailPage /></TradingRoute>
        } />
        <Route path="trading/accounting" element={
          <TradingRoute module="ACCOUNTING"><TradingAccountingPage /></TradingRoute>
        } />
        <Route path="trading/accounting/accounts/:id" element={
          <TradingRoute module="ACCOUNTING"><TradingAccountDetailPage /></TradingRoute>
        } />
        <Route path="trading/accounting/journal-entries/new" element={
          <TradingRoute module="ACCOUNTING" action="CREATE"><TradingJournalEntryFormPage /></TradingRoute>
        } />
        <Route path="trading/accounting/journal-entries/:id" element={
          <TradingRoute module="ACCOUNTING"><TradingJournalEntryDetailPage /></TradingRoute>
        } />
        <Route path="trading/accounting/journal-entries/:id/edit" element={
          <TradingRoute module="ACCOUNTING" action="EDIT"><TradingJournalEntryFormPage /></TradingRoute>
        } />
        <Route path="trading/accounting/reports" element={
          <TradingRoute module="ACCOUNTING"><TradingReportsPage /></TradingRoute>
        } />
        <Route path="trading/accounting/periods" element={
          <TradingRoute module="ACCOUNTING"><TradingFiscalPeriodsPage /></TradingRoute>
        } />
        <Route path="trading/accounting/posting-rules" element={
          <TradingRoute module="ACCOUNTING"><TradingPostingRulesPage /></TradingRoute>
        } />
        <Route path="trading/accounting/tie-out" element={
          <TradingRoute module="ACCOUNTING"><TradingTieOutPage /></TradingRoute>
        } />
        <Route path="trading/accounting/period-end" element={
          <TradingRoute module="ACCOUNTING"><TradingPeriodEndPage /></TradingRoute>
        } />
        <Route path="trading/accounting/activity" element={
          <TradingRoute module="ACCOUNTING"><TradingActivityFeedPage /></TradingRoute>
        } />

        {/* Settings Routes (Admin Protected) */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/team" element={
          <AdminGuard><TeamSettingsPage /></AdminGuard>
        } />
        <Route path="settings/email-templates" element={
          <AdminGuard><EmailTemplatesPage /></AdminGuard>
        } />
        <Route path="settings/payment-plan-templates" element={
          <AdminGuard><PaymentPlanTemplatesPage /></AdminGuard>
        } />
        <Route path="settings/permissions" element={
          <AdminGuard><UserPermissionsPage /></AdminGuard>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/travel" replace />} />
    </Routes>
  );
}

export default App;
